import { api } from "@/lib/api";

export interface DokumentDjelatnika {
  trajno?: boolean;
  confirmation_label?: string | null;
  display_title?: string | null;
  archive_category?: string;
  created_at?: string | null;
  naziv?: string;
  opis?: string | null;
  structured_record_kind?: string | null;
  structured_record_id?: string | null;
  structured_record_label?: string | null;
  id: string;
  radnik_id: string;
  document_type_id: string;
  document_type_code: string | null;
  document_type_name: string | null;
  expiry_required: boolean;
  issued_on: string | null;
  expires_on: string | null;
  verificiran: boolean;
  verificirao_id: string | null;
  verificirano_at: string | null;
  napomena: string | null;
  status: "active" | "superseded" | "deleted";
  deleted_at: string | null;
  has_content: boolean;
  mime_type: string | null;
  original_filename: string | null;
}

export interface VerzijaTipaDokumenta {
  id: string;
  name_hr: string;
  requirement_level: "required" | "conditional" | "reference_only";
  expiry_required: boolean;
  status: "draft_legal_review" | "active" | "retired";
}

export interface TipDokumenta {
  id: string;
  code: string;
  business_purpose: string;
  document_group?: string | null;
  active_version: VerzijaTipaDokumenta | null;
}

export interface AiDocumentVerificationFeature {
  enabled: boolean;
  confidence_threshold: number;
}

export interface DocumentAiSuggestion {
  id: string;
  employee_document_id: string;
  model: string;
  ekstrahirana_polja: Record<string, unknown>;
  confidence: number;
  podudarnost_profila: "podudara_se" | "djelomicno" | "ne_podudara_se";
  status: "predlozeno" | "prihvaceno" | "odbaceno" | "isteklo";
  odlucio_id: string | null;
  odluceno_at: string | null;
  created_at: string;
}

export async function dohvatiAiPostavke(
  signal?: AbortSignal,
): Promise<AiDocumentVerificationFeature> {
  const response = await api.get<{
    ai_document_verification: AiDocumentVerificationFeature;
  }>("/features", { signal });
  return response.data.ai_document_verification;
}

/** Minutes of inactivity a session survives, as the server counts them. */
export async function dohvatiMinuteNeaktivnosti(
  signal?: AbortSignal,
): Promise<number> {
  const response = await api.get<{ session: { idle_minutes: number } }>(
    "/features",
    { signal },
  );
  return response.data.session.idle_minutes;
}

export async function dohvatiAiPrijedlog(
  radnikId: string,
  documentId: string,
  signal?: AbortSignal,
): Promise<DocumentAiSuggestion | null> {
  const response = await api.get<DocumentAiSuggestion | null>(
    `/radnici/${radnikId}/documents/${documentId}/ai-suggestion`,
    { signal },
  );
  return response.data;
}

export async function odluciOAIPrijedlogu(
  radnikId: string,
  documentId: string,
  decision: "prihvaceno" | "odbaceno",
): Promise<{
  suggestion: DocumentAiSuggestion;
  prefill_expires_on: string | null;
}> {
  const response = await api.post<{
    suggestion: DocumentAiSuggestion;
    prefill_expires_on: string | null;
  }>(`/radnici/${radnikId}/documents/${documentId}/ai-suggestion/decision`, {
    decision,
  });
  return response.data;
}

/**
 * Documents on an employee's record.
 *
 * Deleted ones are left out unless asked for, and asking is only answered for
 * whoever may delete a document in the first place.
 */
export async function dohvatiDokumente(
  radnikId: string,
  signal?: AbortSignal,
  ukljuciObrisane = false,
  ukljuciZdravstvene = true,
): Promise<DokumentDjelatnika[]> {
  const response = await api.get<DokumentDjelatnika[]>(
    `/radnici/${radnikId}/documents`,
      { signal, params: ukljuciObrisane || !ukljuciZdravstvene ? { ...(ukljuciObrisane ? { include_deleted: true } : {}), ...(!ukljuciZdravstvene ? { include_health: false } : {}) } : undefined },
  );
  return response.data;
}

export async function dohvatiTipoveDokumenata(
  signal?: AbortSignal,
): Promise<TipDokumenta[]> {
  const response = await api.get<TipDokumenta[]>("/document-types", { signal });
  return response.data;
}

export async function ucitajDokument(
  radnikId: string,
  podaci: {
    documentTypeCode: string;
    naziv: string;
    opis?: string;
    datoteka: File;
    issuedOn?: string;
    expiresOn?: string;
    qualificationTrajno?: boolean;
  },
): Promise<DokumentDjelatnika> {
  const tijelo = new FormData();
  tijelo.append("document_type_code", podaci.documentTypeCode);
  tijelo.append("datoteka", podaci.datoteka);
  tijelo.append("naziv", podaci.naziv);
  if (podaci.opis) tijelo.append("opis", podaci.opis);
  if (podaci.issuedOn) tijelo.append("issued_on", podaci.issuedOn);
  if (podaci.expiresOn) tijelo.append("expires_on", podaci.expiresOn);
  if (podaci.qualificationTrajno) tijelo.append("qualification_trajno", "true");
  const response = await api.post<DokumentDjelatnika>(
    `/radnici/${radnikId}/documents/upload`,
    tijelo,
  );
  return response.data;
}

export async function dohvatiSadrzajDokumenta(
  radnikId: string,
  documentId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await api.get(
    `/radnici/${radnikId}/documents/${documentId}/content`,
    { responseType: "blob", signal },
  );
  return response.data as Blob;
}

export async function verificirajDokument(
  radnikId: string,
  documentId: string,
  podaci: { expires_on?: string | null; napomena?: string | null },
): Promise<DokumentDjelatnika> {
  const response = await api.post<DokumentDjelatnika>(
    `/radnici/${radnikId}/documents/${documentId}/verify`,
    podaci,
  );
  return response.data;
}

export interface DocumentRecordDetails {
  kind: string;
  id: string;
  tab: string;
  can_edit: boolean;
  fields: { key: string; label: string; value: string | null }[];
}

export interface EmployeeDocumentDetails {
  document: DokumentDjelatnika;
  verificirao_name: string | null;
  structured_record: DocumentRecordDetails | null;
  structured_record_restricted: boolean;
}

export async function dohvatiPodatkeDokumenta(
  radnikId: string, documentId: string, signal?: AbortSignal,
): Promise<EmployeeDocumentDetails> {
  const response = await api.get<EmployeeDocumentDetails>(
    `/radnici/${radnikId}/documents/${documentId}/details`, { signal },
  );
  return response.data;
}

export async function renameEmployeeDocument(radnikId: string, documentId: string, naziv: string): Promise<DokumentDjelatnika> {
  return (await api.patch<DokumentDjelatnika>(`/radnici/${radnikId}/documents/${documentId}/title`, { naziv })).data;
}

export async function obrisiDokument(radnikId: string, documentId: string, reason: string): Promise<DokumentDjelatnika> {
  return (await api.delete<DokumentDjelatnika>(`/radnici/${radnikId}/documents/${documentId}`, { data: { reason } })).data;
}

export async function vratiDokument(radnikId: string, documentId: string): Promise<DokumentDjelatnika> {
  return (await api.post<DokumentDjelatnika>(`/radnici/${radnikId}/documents/${documentId}/restore`)).data;
}

export async function premjestiDokument(radnikId: string, documentId: string, noviRadnikId: string): Promise<DokumentDjelatnika> {
  return (await api.post<DokumentDjelatnika>(`/radnici/${radnikId}/documents/${documentId}/reassign`, { radnik_id: noviRadnikId })).data;
}
